import { RootState, useAppDispatch } from '@AppState/redux-store'
import { useSelector } from 'react-redux'

import { AnimationBackground } from '@Components/animation/background/background'
import { AnimationActions, ANIMATION_PLAY_STATE } from '@AppState/animation'

import { useCallback, useEffect, useState } from 'react'
import { AnimationAddOnPanelProps } from '@Components/animation/add-on-panel/animation-add-on-panel'

import './styles.scss'
import { useScreenWake } from '@Util/screen-wake.ts'
import { AwaitingNextDrawModalProps } from '@Components/animation/awaiting-next-draw/awaiting-next-draw'
import { WatchAnimation } from './watch-animation'
import { useTimeout } from '@Hooks/use-timeout'
import { ViewModeController } from '@Components/animation/controls/view-mode-animation-controls'
import { AwaitingNextDrawUtils } from '@Components/animation/awaiting-next-draw/utils'
import { AnimationAddOnPanelUtils } from '@Components/animation/add-on-panel/utils'

const CLASS_NAME = 'gby-k2g-watch-animation'

export const ViewMode = () => {
  const latestDraw = useSelector((state: RootState) => state.latestDraw.latestDraw)
  const viewModeData = useSelector((state: RootState) => state.viewMode)
  const { playState, showAddonPanel } = useSelector((state: RootState) => state.animation)
  const addOnConfigs = useSelector((state: RootState) => state.hostConfig.config?.addOns ?? [])
  const { appSettings } = useSelector((state: RootState) => state.appSettings)

  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)

  const autoPlay = () => {
    dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.READY))
    setDrawNumber(drawNumber + 1)
  }
  const { clear: clearAutoPlay, setTimeoutDelay: setAutoPlayDelay } = useTimeout(autoPlay, null)

  useScreenWake()

  const dispatch = useAppDispatch()

  const [drawNumber, setDrawNumber] = useState<number>(latestDraw ?? 0)
  const [addOnPanelProps, setAddOnPanelProps] = useState<AnimationAddOnPanelProps>({
    display: showAddonPanel,
    multiplierAddOn: undefined,
    miniGameAddOn: undefined,
  })

  const animationReady =
    playState !== ANIMATION_PLAY_STATE.GAME_LOADING &&
    latestDraw !== null &&
    viewModeData.drawResults[drawNumber] !== undefined

  const drawNumberChangeHandler = useCallback(
    (num: number) => {
      setDrawNumber(num)
      dispatch(AnimationActions.setAddOnPanelState(false))
    },
    [dispatch],
  )

  useEffect(() => {
    if (!drawNumber) return
    const result = viewModeData.drawResults[drawNumber]
    if (!result) return

    const newProps = AnimationAddOnPanelUtils.mapUnscoredResultToProps(
      result,
      addOnConfigs,
      showAddonPanel,
    )
    setAddOnPanelProps(newProps)
  }, [drawNumber, addOnConfigs, viewModeData, showAddonPanel])

  useEffect(() => {
    if (playState === ANIMATION_PLAY_STATE.FINISHED && latestDraw) {
      if (appSettings.autoPlay) {
        setAutoPlayDelay(5000)
      }
    } else {
      clearAutoPlay()
    }
  }, [
    appSettings.autoPlay,
    playState,
    dispatch,
    drawNumber,
    latestDraw,
    clearAutoPlay,
    setAutoPlayDelay,
  ])

  let awaitingNextDrawProps: AwaitingNextDrawModalProps | null = null

  if (latestDraw && drawNumber > latestDraw && !viewModeData.drawResults[drawNumber]) {
    const lastDraw = viewModeData.drawResults[drawNumber - 1]

    if (lastDraw) {
      awaitingNextDrawProps = AwaitingNextDrawUtils.mapDrawToProps(lastDraw, addOnConfigs)
    }
  }

  return (
    <div className={CLASS_NAME}>
      <AnimationBackground />

      <WatchAnimation
        animationReady={animationReady}
        adaDescription={
          viewModeData.drawResults && viewModeData.drawResults[drawNumber]
            ? `Keno ${hostConfig?.animationDrawInfo.watchAnimationAdaDescriptionDrawing ?? 'Drawing'} #${drawNumber} results animated below: ${viewModeData.drawResults[drawNumber].winningNumbers}`
            : null
        }
        addOnPanelProps={addOnPanelProps}
        awaitingNextDrawProps={awaitingNextDrawProps}
        controls={
          <ViewModeController
            animationReady={animationReady}
            currentDrawNumber={drawNumber ?? 0}
            viewModeData={viewModeData}
            latestDraw={latestDraw ?? 0}
            onDrawingChange={drawNumberChangeHandler}
          />
        }
        title={hostConfig?.animationDrawInfo.watchAnimationTitle ?? 'Watch Drawing'}
      />
    </div>
  )
}
